import './index.css';
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { t } from '../../services/locale';
import { getIcon } from '../../utils/icon-manager';

export type SectionDockType = 'face' | 'axis' | 'box';
export type SectionDockAxis = 'x' | 'y' | 'z';

export interface SectionDockPanelOptions {
    defaultType?: SectionDockType | null;
    defaultAxis?: SectionDockAxis;
    defaultHidden?: boolean;
    defaultFill?: boolean;
    onTypeChange?: (type: SectionDockType, axis: SectionDockAxis) => void;
    onAxisChange?: (axis: SectionDockAxis) => void;
    onHideToggle?: (hidden: boolean) => void;
    onReverse?: () => void;
    onReset?: (type: SectionDockType, axis: SectionDockAxis) => void;
    onFitToModel?: () => void;
    onFillToggle?: (isFilled: boolean) => void;
}

interface ToolDefinition {
    key: 'hide' | 'reverse' | 'reset' | 'fit' | 'fill';
    iconName: string;
    textKey: string;
    onClick: () => void;
    isActive?: boolean;
}

export class SectionDockPanel implements IBimComponent {
    public readonly element: HTMLElement;
    private readonly options: SectionDockPanelOptions;

    private readonly typeButtons: Map<SectionDockType, HTMLButtonElement> = new Map();
    private readonly axisButtons: Map<SectionDockAxis, HTMLButtonElement> = new Map();
    private readonly toolContainer: HTMLElement;
    private readonly axisPanel: HTMLElement;
    private readonly divider: HTMLElement;

    private activeType: SectionDockType | null;
    private activeAxis: SectionDockAxis;
    private isHidden: boolean;
    private isFilled: boolean;

    constructor(options: SectionDockPanelOptions = {}) {
        this.options = options;
        this.activeType = options.defaultType ?? null;
        this.activeAxis = options.defaultAxis ?? 'x';
        this.isHidden = options.defaultHidden ?? false;
        this.isFilled = options.defaultFill ?? false;

        const { root, toolContainer, axisPanel, divider } = this.createDom();
        this.element = root;
        this.toolContainer = toolContainer;
        this.axisPanel = axisPanel;
        this.divider = divider;
    }

    public init(): void {
        this.applyTypeState();
        this.applyAxisState();
        this.renderTools();
        this.setLocales();
    }

    public setTheme(theme: ThemeConfig): void {
        const style = this.element.style;
        style.setProperty('--bim-text-primary', theme.textPrimary);
        style.setProperty('--bim-text-secondary', theme.textSecondary);
        style.setProperty('--bim-border-default', theme.borderDefault);
        style.setProperty('--bim-border-strong', theme.borderStrong);
        style.setProperty('--bim-bg-inset', theme.bgInset);
        style.setProperty('--bim-bg-elevated', theme.bgElevated);
        style.setProperty('--bim-primary', theme.primary);
        style.setProperty('--bim-primary-subtle', theme.primarySubtle);
        style.setProperty('--bim-component-bg-hover', theme.componentBgHover);
    }

    public setLocales(): void {
        this.typeButtons.get('face')!.dataset.tooltip = t('toolbar.sectionPlane');
        this.typeButtons.get('axis')!.dataset.tooltip = t('toolbar.sectionAxis');
        this.typeButtons.get('box')!.dataset.tooltip = t('toolbar.sectionBox');

        this.axisButtons.get('x')!.dataset.label = 'X';
        this.axisButtons.get('y')!.dataset.label = 'Y';
        this.axisButtons.get('z')!.dataset.label = 'Z';

        this.typeButtons.forEach((button) => {
            button.setAttribute('aria-label', button.dataset.tooltip ?? '');
        });
        this.axisButtons.forEach((button) => {
            button.setAttribute('aria-label', button.dataset.label ?? '');
        });

        this.renderTools();
    }

    public destroy(): void {
        this.element.remove();
    }

    public resetForOpen(): void {
        this.activeType = null;
        this.isHidden = false;
        this.applyTypeState();
        this.renderTools();
    }

    public getActiveType(): SectionDockType | null {
        return this.activeType;
    }

    public getActiveAxis(): SectionDockAxis {
        return this.activeAxis;
    }

    public setFillState(isFilled: boolean): void {
        this.isFilled = isFilled;
        this.renderTools();
    }

    public getFillState(): boolean {
        return this.isFilled;
    }

    private createDom(): { root: HTMLElement; toolContainer: HTMLElement; axisPanel: HTMLElement; divider: HTMLElement } {
        const root = document.createElement('div');
        root.className = 'section-dock-panel';

        const axisPanel = document.createElement('div');
        axisPanel.className = 'section-dock-axis-panel';

        (['x', 'y', 'z'] as SectionDockAxis[]).forEach((axis) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'section-dock-axis-btn';
            button.textContent = axis.toUpperCase();
            button.addEventListener('click', () => {
                this.handleAxisChange(axis);
            });
            this.axisButtons.set(axis, button);
            axisPanel.appendChild(button);
        });

        const main = document.createElement('div');
        main.className = 'section-dock-main';

        const left = document.createElement('div');
        left.className = 'section-dock-types';

        const faceBtn = this.createTypeButton('face', getIcon('拾曲面剖切'));
        const axisBtn = this.createTypeButton('axis', getIcon('轴向剖切'));
        const boxBtn = this.createTypeButton('box', getIcon('剖切盒'));

        left.appendChild(faceBtn);
        left.appendChild(axisBtn);
        left.appendChild(boxBtn);

        const divider = document.createElement('div');
        divider.className = 'section-dock-divider';

        const toolContainer = document.createElement('div');
        toolContainer.className = 'section-dock-tools';

        main.appendChild(left);
        main.appendChild(divider);
        main.appendChild(toolContainer);

        root.appendChild(axisPanel);
        root.appendChild(main);

        return { root, toolContainer, axisPanel, divider };
    }

    private createTypeButton(type: SectionDockType, iconSvg: string): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'section-dock-type-btn';

        const icon = document.createElement('span');
        icon.className = 'section-dock-type-icon';
        icon.innerHTML = iconSvg;

        button.appendChild(icon);
        button.addEventListener('click', () => {
            this.handleTypeChange(type);
        });

        this.typeButtons.set(type, button);
        return button;
    }

    private handleTypeChange(type: SectionDockType): void {
        if (this.activeType === type) {
            return;
        }

        this.activeType = type;
        this.applyTypeState();
        this.renderTools();

        if (this.isHidden) {
            this.isHidden = false;
            this.options.onHideToggle?.(false);
        }

        this.options.onTypeChange?.(type, this.activeAxis);
    }

    private handleAxisChange(axis: SectionDockAxis): void {
        if (this.activeAxis === axis) {
            return;
        }

        this.activeAxis = axis;
        this.applyAxisState();

        if (this.activeType === 'axis') {
            this.options.onAxisChange?.(axis);
        }
    }

    private applyTypeState(): void {
        this.typeButtons.forEach((button, type) => {
            button.classList.toggle('is-active', type === this.activeType);
        });

        const axisVisible = this.activeType === 'axis';
        this.axisPanel.classList.toggle('is-visible', axisVisible);
    }

    private applyAxisState(): void {
        this.axisButtons.forEach((button, axis) => {
            button.classList.toggle('is-active', axis === this.activeAxis);
        });
    }

    private renderTools(): void {
        this.toolContainer.innerHTML = '';

        if (!this.activeType) {
            this.divider.classList.add('is-hidden');
            return;
        }

        this.divider.classList.remove('is-hidden');

        this.getToolsForType(this.activeType).forEach((tool) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'section-dock-tool-btn';
            if (tool.isActive) {
                button.classList.add('is-active');
            }

            const icon = document.createElement('span');
            icon.className = 'section-dock-tool-icon';
            icon.innerHTML = getIcon(tool.iconName);

            button.appendChild(icon);
            const text = t(tool.textKey);
            button.dataset.tooltip = text;
            button.setAttribute('aria-label', text);
            button.addEventListener('click', tool.onClick);

            this.toolContainer.appendChild(button);
        });
    }

    private getToolsForType(type: SectionDockType): ToolDefinition[] {
        const fillTool: ToolDefinition = {
            key: 'fill',
            iconName: '填充',
            textKey: type === 'box' ? 'sectionBox.actions.fill' : type === 'axis' ? 'sectionAxis.actions.fill' : 'sectionPlane.actions.fill',
            isActive: this.isFilled,
            onClick: () => {
                this.isFilled = !this.isFilled;
                this.options.onFillToggle?.(this.isFilled);
                this.renderTools();
            }
        };

        const hideTool: ToolDefinition = {
            key: 'hide',
            iconName: '隐藏',
            textKey: type === 'box' ? 'sectionBox.actions.hide' : type === 'axis' ? 'sectionAxis.actions.hide' : 'sectionPlane.actions.hide',
            isActive: this.isHidden,
            onClick: () => {
                this.isHidden = !this.isHidden;
                this.options.onHideToggle?.(this.isHidden);
                this.renderTools();
            }
        };

        const reverseTool: ToolDefinition = {
            key: 'reverse',
            iconName: '反向',
            textKey: type === 'axis' ? 'sectionAxis.actions.reverse' : type === 'box' ? 'sectionBox.actions.reverse' : 'sectionPlane.actions.reverse',
            onClick: () => {
                this.options.onReverse?.();
            }
        };

        if (type === 'axis') {
            return [
                fillTool,
                hideTool,
                reverseTool,
                {
                    key: 'reset',
                    iconName: '重置',
                    textKey: 'sectionAxis.actions.reset',
                    onClick: () => {
                        this.isHidden = false;
                        this.options.onReset?.(type, this.activeAxis);
                        this.renderTools();
                    }
                }
            ];
        }

        if (type === 'box') {
            return [
                fillTool,
                hideTool,
                {
                    key: 'fit',
                    iconName: '适应到模型',
                    textKey: 'sectionBox.actions.fitToModel',
                    onClick: () => {
                        this.options.onFitToModel?.();
                    }
                },
                {
                    key: 'reset',
                    iconName: '重置',
                    textKey: 'sectionBox.actions.reset',
                    onClick: () => {
                        this.isHidden = false;
                        this.options.onReset?.(type, this.activeAxis);
                        this.renderTools();
                    }
                }
            ];
        }

        return [
            fillTool,
            hideTool,
            reverseTool,
            {
                key: 'reset',
                iconName: '重置',
                textKey: 'sectionPlane.actions.reset',
                onClick: () => {
                    this.isHidden = false;
                    this.options.onReset?.(type, this.activeAxis);
                    this.renderTools();
                }
            }
        ];
    }
}
